import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { notificationSettingsService } from '../../services/notificationSettingsService';
import { psychologistService } from '../../services/psychologistService';
import { SettingToggle } from './components/SettingToggle';
import { ReminderHoursSelector } from './components/ReminderHoursSelector';
import SidebarNavigation from '../../components/navigation/SidebarNavigation';

export default function NotificationSettingsScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [psychologistId, setPsychologistId] = useState(null);
  
  // Settings state
  const [settings, setSettings] = useState({
    notificationSoundEnabled: true,
    whatsappReminderHours: 24,
    notifyNewAppointment: true,
    notifyCancelledAppointment: true,
    notifyDailyAppointments: true,
    publicPageEnabled: false
  });

  // Load psychologist ID and settings on mount
  useEffect(() => {
    loadPsychologistSettings();
  }, [user]);

  const loadPsychologistSettings = async () => {
    try {
      setLoading(true);
      setError('');

      if (!user?.id) {
        setError('Usuário não autenticado');
        return;
      }

      // Get psychologist ID from user ID - FIXED: Use correct method name and handle response format
      const { data: psychologist, error: psychologistError } = await psychologistService?.getPsychologistByUserId(user?.id);
      
      if (psychologistError) {
        throw psychologistError;
      }
      
      if (!psychologist?.id) {
        setError('Perfil de psicólogo não encontrado');
        return;
      }

      setPsychologistId(psychologist?.id);

      // Load notification settings
      const currentSettings = await notificationSettingsService?.getSettings(psychologist?.id);
      setSettings(currentSettings);

    } catch (err) {
      console.error('Error loading settings:', err);
      setError(err?.message || 'Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      if (!psychologistId) {
        setError('ID do psicólogo não encontrado');
        return;
      }

      await notificationSettingsService?.updateSettings(psychologistId, settings);
      
      setSuccessMessage('Configurações salvas com sucesso!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err?.message || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#c3c3c3' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando configurações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: '#d3d3d3' }}>
      <SidebarNavigation />
      <main className="flex-1 lg:ml-64">
        <div className="p-3 sm:p-4 lg:p-4 pt-4 sm:pt-4 lg:pt-6 lg:mt-0 max-w-full overflow-x-hidden">
          {/* Header - Standardized Format */}
          <div className="mb-8">
            <div className="bg-gray-100 rounded-xl shadow-lg shadow-md p-6 max-w-6xl">

              {/* Desktop */}
              <div className="hidden md:flex items-center gap-3 mb-2 flex-nowrap">
                <Bell className="w-8 h-8 text-[#969696]" />

                <h1 className="text-3xl font-bold text-[#969696] font-manrope whitespace-nowrap">
                  Notificações
                </h1>
              </div>

              {/* Mobile */}
              <div className="md:hidden flex items-center gap-2 mb-1">
                <Bell className="w-8 h-8 text-[#969696]" />

                <h1 className="text-2xl font-bold text-[#969696] font-manrope">
                  Notificações
                </h1>
              </div>

              {/* Desktop - Mostra subtítulo */}
              <p className="hidden md:block text-[#969696] font-manrope text-lg">
                Defina o modo das notificações
              </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Erro</p>
              <p className="text-sm text-red-600 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          </div>
        )}

        {/* Settings Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            {/* WhatsApp Reminder Hours */}
            <ReminderHoursSelector
              value={settings?.whatsappReminderHours}
              onChange={(value) => handleSettingChange('whatsappReminderHours', value)}
              disabled={saving}
            />

            {/* New Appointment Notification */}
            <SettingToggle
              id="notify-new-appointment"
              label="Novos Agendamentos"
              description="Receber notificação quando um novo agendamento for feito"
              checked={settings?.notifyNewAppointment}
              onChange={(value) => handleSettingChange('notifyNewAppointment', value)}
              disabled={saving}
            />

            {/* Cancelled Appointment Notification */}
            <SettingToggle
              id="notify-cancelled-appointment"
              label="Agendamentos Cancelados"
              description="Receber notificação quando um agendamento for cancelado"
              checked={settings?.notifyCancelledAppointment}
              onChange={(value) => handleSettingChange('notifyCancelledAppointment', value)}
              disabled={saving}
            />

            {/* Daily Appointments Reminder */}
            <SettingToggle
              id="notify-daily-appointments"
              label="Lembrete Diário"
              description="Receber um lembrete sobre quantos agendamentos você tem no dia de hoje"
              checked={settings?.notifyDailyAppointments}
              onChange={(value) => handleSettingChange('notifyDailyAppointments', value)}
              disabled={saving}
            />

            {/* Public Page Enabled */}
            <SettingToggle
              id="public-page-enabled"
              label="Página Pública de Agendamentos"
              description="Habilitar a página pública para que novos pacientes possam agendar consultas online"
              checked={settings?.publicPageEnabled}
              onChange={(value) => handleSettingChange('publicPageEnabled', value)}
              disabled={saving}
            />
          </div>
        </div>

          {/* Save Button */}
          <div className="flex justify-end gap-4 pt-4 bg-transparent">
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Salvando...' : 'Salvar Configurações'}
            </button>
          </div>


        {/* Help Text */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Dica:</strong> As configurações de notificação ajudam você a manter-se informado sobre seus agendamentos 
            e a gerenciar melhor sua agenda. Você pode ajustar essas configurações a qualquer momento.
          </p>
        </div>
      </div>
      </main>
    </div>
  );
}